<template>
  <div class="flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white">
    <div class="shrink-0 flex items-center gap-4 h-9 px-4 border-b border-gray-200 bg-growth-10 text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
      <span class="flex-1">Question</span>
      <span v-if="showUser" class="w-40">Asked by</span>
      <span class="w-28">Type</span>
      <span class="w-24 text-right">When</span>
    </div>

    <ul v-if="loading" class="flex-1 min-h-0 divide-y divide-gray-200">
      <li v-for="n in 6" :key="n" class="flex items-center gap-4 h-11 px-4 animate-pulse">
        <div class="h-3 flex-1 bg-gray-100 rounded"></div>
        <div class="h-3 w-28 bg-gray-100 rounded"></div>
        <div class="h-3 w-24 bg-gray-100 rounded"></div>
      </li>
    </ul>

    <p v-else-if="jobs.length === 0" class="px-4 py-4 text-[13px] text-gray-600">
      {{ emptyText }}
    </p>

    <ul v-else class="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-200">
      <li v-for="job in jobs" :key="job.id">
        <button
          type="button"
          class="w-full flex items-center gap-4 h-11 px-4 text-left transition-colors focus:outline-none focus-visible:bg-primary-50"
          :class="job.id === activeJobId ? 'bg-primary-50' : 'hover:bg-growth-25'"
          @click="$emit('select', job)"
        >
          <span class="flex-1 min-w-0 truncate text-[13px] text-gray-900">
            <span v-if="showEntity && job.entity_name" class="sensitive font-medium">{{ job.entity_name }} · </span>
            <span class="sensitive" data-demo-blur>{{ job.query }}</span>
          </span>
          <span v-if="showUser" class="sensitive shrink-0 w-40 truncate text-[13px] text-gray-600">{{ job.user_name }}</span>
          <span class="shrink-0 w-28 text-[13px] text-gray-600">{{ typeLabel(job.job_type) }}</span>
          <span class="shrink-0 w-24 text-right text-[13px]">
            <span v-if="isPending(job)" class="inline-flex items-center gap-2 font-medium text-primary-700">
              <span class="h-1.5 w-1.5 rounded-full bg-primary-500 animate-pulse" aria-hidden="true"></span>
              {{ job.status === 'QUEUED' ? 'Queued' : 'Analyzing' }}
            </span>
            <span v-else-if="job.status === 'FAILED'" class="font-medium text-red-dark">Failed</span>
            <span v-else class="text-gray-600">{{ formatDate(job.created_at) }}</span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
const TYPE_LABELS = {
  personal_analyst: 'Analysis',
  weekly_report: 'Weekly report',
  pre_meeting_brief: 'Brief',
  peer_practice_playbook: 'Playbook',
}

export default {
  name: 'AnalystHistory',
  props: {
    jobs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    activeJobId: {
      type: [String, Number],
      default: null,
    },
    showUser: {
      type: Boolean,
      default: false,
    },
    showEntity: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'Your questions and reports will appear here.',
    },
  },
  emits: ['select'],
  methods: {
    typeLabel(jobType) {
      return TYPE_LABELS[jobType] || jobType
    },
    isPending(job) {
      return job.status === 'QUEUED' || job.status === 'PROCESSING'
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)
      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`
      const sameYear = date.getFullYear() === now.getFullYear()
      return date.toLocaleDateString('en-US', sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' })
    },
  },
}
</script>
